const DATASET_NAME_ERROR_TABLE_ROW = 'errorAlertBoxTableRow';
const COLOR_GRAYSCALE_400 = '#dadada';

const handleHttpErrorsFromResponse = response => {
	if (!response.ok) {
		// Rather than having to handle errors twice in the fetch-chain, we make sure that
		// also when the response.ok is false the "catch" block can handle the error by
		// "transforming" a non-2xx-response from the server into an error:
		throw new Error('Service temporarily not available');
	}

	return response;
};

const getTableRowForErrorAlert = () => {
	const tableRow = document.createElement('tr');
	// Ensure that we can programmatically detect already existing error alert boxes / table rows:
	tableRow.dataset[DATASET_NAME_ERROR_TABLE_ROW] = true;

	return tableRow;
};

const getTableDataForErrorAlert = () => {
	const tableData = document.createElement('td');
	tableData.classList.add('c-table__data--compact', 'c-table__data--vertical-align-middle');
	tableData.setAttribute('colspan', '3');
	tableData.style.padding = '0';
	tableData.style.borderBottom = 'none';

	return tableData;
};

const getErrorAlertForTable = contentElement => {
	const errorTableRow = getTableRowForErrorAlert();
	const errorTableData = getTableDataForErrorAlert();

	errorTableData.appendChild(contentElement);
	errorTableRow.appendChild(errorTableData);

	return errorTableRow;
};

const getRestyledCloneOfAlertBox = errorAlertBox => {
	const clone = errorAlertBox.cloneNode(true);
	// If the alert box is not being displayed due to an error received during render time,
	// one will still be in the DOM, but hidden, so we unhide it:
	clone.removeAttribute('hidden');
	clone.style.borderTop = 'none';
	clone.style.borderRight = 'none';
	clone.style.borderLeft = 'none';
	clone.style.margin = '0';
	// The following style is meant for proper alignment of the icon and the status message
	// with the numbering and journal titles in the other lines, as requested by Sebastian Miller:
	clone.querySelector('.eds-c-status-message__icon').style.marginRight = '28px';

	return clone;
};

const showErrorFor = formElement => {
	const errorAlertBox = document.querySelector('#alert-message-quote-request-failed');
	const tableRowWithFormElement = formElement.closest('tr');

	if (errorAlertBox === null || tableRowWithFormElement === null) {
		return;
	}

	const errorAlertBoxCopy = getRestyledCloneOfAlertBox(errorAlertBox);
	const errorTableRow = getErrorAlertForTable(errorAlertBoxCopy);

	tableRowWithFormElement.after(errorTableRow);

	for (const tableData of tableRowWithFormElement.querySelectorAll('td')) {
		tableData.style.borderBottom = 'none';
	}
};

const showSuccessFor = (formElement, requestDateString = new Date()) => {
	const localeForDesiredFormat = 'en-GB';
	const dateFormattingOptions = {
		day: 'numeric',
		month: 'short',
		year: 'numeric'
	};

	formElement.parentElement.innerHTML = `
		<span class="c-table__data--line-clamp-2" tabindex="0">
			<svg  aria-hidden="true" width="16" height="16" class="c-inputgroup__validation-icon c-inputgroup__validation-icon--done">
				<use href="/static/img/svg-sprite.svg#icon-tick"></use>
			</svg>
			Price requested<br>
			${new Date(requestDateString).toLocaleDateString(localeForDesiredFormat, dateFormattingOptions)}
		</span>`;
};

const createFetchOptions = formElement => {
	return {
		method: formElement.method.toUpperCase(),
		body: new FormData(formElement),
		headers: {
			// Important: If we restrict what we accept, then the backend server will respond with a 406 status code
			// Therefore, we explicitly accept all (leaving this part off will also get us a 406):
			Accept: '*/*'
		}
	};
};

const deleteErrorAlertBoxIfPresentFor = formElement => {
	const formElementTableRow = formElement.closest('tr');
	const errorAlertBoxTableRow = formElementTableRow.nextElementSibling;

	if (errorAlertBoxTableRow !== null && DATASET_NAME_ERROR_TABLE_ROW in errorAlertBoxTableRow.dataset) {
		errorAlertBoxTableRow.remove();

		for (const tableData of errorAlertBoxTableRow.querySelectorAll('td')) {
			tableData.style.borderBottom = `1px solid ${COLOR_GRAYSCALE_400};`;
		}
	}
};

const toggleFormButtonDisabledIfPresent = (formButton, disable) => {
	if (formButton === null) {
		return;
	}

	if (typeof disable !== 'boolean') {
		disable = !formButton.disabled;
	}

	formButton.disabled = disable;
};

const toggleAriaBusy = (ariaLiveElement, isBusy) => {
	if (ariaLiveElement === null) {
		return;
	}

	if (typeof isBusy !== 'boolean') {
		isBusy = !ariaLiveElement.getAttribute('aria-busy') === 'true';
	}

	ariaLiveElement.setAttribute('aria-busy', isBusy ? 'true' : 'false');
};

const submitFormData = formElement => {
	const fetchOptions = createFetchOptions(formElement);
	const formButton = formElement.querySelector('button');
	const ariaLiveElement = formElement.closest('[aria-live]');

	deleteErrorAlertBoxIfPresentFor(formElement);
	toggleFormButtonDisabledIfPresent(formButton, true);
	toggleAriaBusy(ariaLiveElement, true);

	fetch(`${formElement.action}?json`, fetchOptions)
		.then(handleHttpErrorsFromResponse)
		.then(response => response.json())
		.then(responseData => {
			if (responseData.confirmedChangeRequests[0] === 'quote_request_successful') {
				const journalId = fetchOptions.body.get('journalId');
				const denialMetrics = responseData.data.denialMetrics.find(metricData => metricData.journalId === journalId);

				if (denialMetrics !== undefined) {
					showSuccessFor(formElement, denialMetrics.quoteRequestedDate);
				}

				return;
			}

			showErrorFor(formElement);
		})
		.catch(error => {
			showErrorFor(formElement);
		})
		.finally(() => {
			toggleFormButtonDisabledIfPresent(formButton, false);
			toggleAriaBusy(ariaLiveElement, false);
		});
};

const askForYourPrice = formEvent => {
	submitFormData(formEvent.target);

	formEvent.preventDefault();
};

const getAskForYourPriceFormsArray = () => {
	const formsArray = Array.from(document.forms);

	return formsArray.filter(({dataset}) => dataset.formName === 'form_ask_for_your_price');
};

const attachEventHandlersTo = (formElementsArray = []) => {
	for (const formElement of formElementsArray) {
		formElement.addEventListener('submit', askForYourPrice);
	}
};

/**
 * Add JS functionality to the "ask for your price" forms on the journal denials
 * page, so that the form data can be sent as fetch requests without having to
 * reload the whole page with server side rendered HTML.
 * Instead, when JS enhanced, the forms will show a loading animation (as the
 * process can take several seconds to complete) and receive the JSON response
 * from the frontend server rather than the fully rendered HMTL.
 * @return {boolean} true when "ask for your price" forms have been found and enriched, false otherwise
 */
export const enhanceAskForPriceForms = () => {
	const formElementsArray = getAskForYourPriceFormsArray();

	if (formElementsArray.length === 0) {
		return false;
	}

	attachEventHandlersTo(formElementsArray);

	return true;
};
